import { colors } from '@apollo/brand';
import { customColors } from '@apollo/orbit';
import React, { useMemo } from 'react';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { SeriesControls } from 'src/components/charts/helpers/SeriesControls';
import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { format } from 'src/lib/format';
import {
  Resolution,
  UI__ServiceReportQuery_service_stats_queryStats,
} from 'src/lib/graphqlTypes/types';

import { PercentileChartType } from './types';

const uncachedLabelByChartType = {
  p50: 'P50 Latency',
  p90: 'P90 Latency',
  p95: 'P95 Latency',
};

interface SubscriptionPercentileChartProps {
  eventsChartData: UI__ServiceReportQuery_service_stats_queryStats[];
  chartType: PercentileChartType;
  resolution: Resolution;
}

export function SubscriptionPercentileChart({
  eventsChartData,
  chartType,
  resolution,
}: SubscriptionPercentileChartProps) {
  const stackConfigs = useMemo(
    (): LineChartStackConfig<UI__ServiceReportQuery_service_stats_queryStats>[] => [
      {
        colorGradientTop: `${colors.primitives.purple[300]}3C`,
        colorGradientBottom: `${colors.primitives.purple[300]}00`,
        primaryColor: customColors.border.beta,
        cursorColor: customColors.brand.accent,
        label: uncachedLabelByChartType[chartType],
        data: eventsChartData,
        hasData: (stats: UI__ServiceReportQuery_service_stats_queryStats) =>
          stats.metrics.uncachedHistogram.totalCount > 0,
        getValue: (stats: UI__ServiceReportQuery_service_stats_queryStats) => ({
          timestamp: stats.timestamp,
          value: Number(stats.metrics.uncachedHistogram[chartType]),
        }),
      },
      {
        colorGradientTop: `${colors.primitives.yellow[300]}3C`,
        colorGradientBottom: `${colors.primitives.yellow[300]}00`,
        primaryColor: customColors.flowchart.a,
        cursorColor: customColors.brand.accent,
        label: 'Cache Latency',
        data: eventsChartData,
        hasData: (stats: UI__ServiceReportQuery_service_stats_queryStats) =>
          stats.metrics.cachedHistogram.totalCount > 0,
        getValue: (stats: UI__ServiceReportQuery_service_stats_queryStats) => ({
          timestamp: stats.timestamp,
          value: Number(stats.metrics.cachedHistogram[chartType]),
        }),
      },
    ],
    [chartType, eventsChartData],
  );

  const lineChartStackInfo = useStackInfo<
    UI__ServiceReportQuery_service_stats_queryStats,
    { value: number },
    LineChartStackConfig<UI__ServiceReportQuery_service_stats_queryStats>
  >(stackConfigs);

  const formatValue = (_: 'major' | 'minor', value: number): string =>
    format.ms(value, 1, false);

  return (
    <>
      <LineChart
        {...lineChartStackInfo}
        stacked
        className="h-36"
        strokeWidth={1}
        resolution={resolution}
        formatValue={formatValue}
        tooltipLabelAs={<InsightsLineChartTooltip formatValue={formatValue} />}
        hideCursorArrow
      />
      <SeriesControls
        className="w-full px-4 pt-6 text-center"
        hideWhenSingleStack
        {...lineChartStackInfo}
      />
    </>
  );
}
